import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Camera, Heart, MessageCircle, Trophy, Upload, Trash2, User } from "lucide-react";
import { toast } from "sonner";
import { getAuthHeaders, hasToken, BACKEND_URL } from "@/lib/api";

const API = `${BACKEND_URL}/api`;

// Auth config helper
const authConfig = () => ({
  withCredentials: !hasToken(),
  headers: getAuthHeaders()
});

export default function SelfieGame({ eventId, isOwner }) {
  const [selfies, setSelfies] = useState([]);
  const [winners, setWinners] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [commentData, setCommentData] = useState({});
  const [userName, setUserName] = useState(localStorage.getItem("selfie_user_name") || "");
  const [tempName, setTempName] = useState("");

  const categories = [
    { value: "all", label: "Alle", emoji: "📸" },
    { value: "funny", label: "Lustigste", emoji: "😂" },
    { value: "group", label: "Gruppen-Selfie", emoji: "👥" },
    { value: "creative", label: "Kreativste", emoji: "🎨" },
    { value: "general", label: "Allgemein", emoji: "✨" }
  ];

  useEffect(() => {
    fetchSelfies();
    fetchWinners();
  }, [eventId]);

  const fetchSelfies = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/selfies`);
      setSelfies(response.data);
    } catch (error) {
      console.error("Failed to fetch selfies:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWinners = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/selfies/winners`);
      setWinners(response.data);
    } catch (error) {
      console.error("Failed to fetch winners:", error);
    }
  };

  const handleUpload = async (e, category) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!userName.trim()) {
      toast.error("Bitte gib deinen Namen ein!");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      toast.loading("Selfie wird hochgeladen...", { id: "selfie-upload" });

      await axios.post(
        `${API}/events/${eventId}/selfies?author_name=${encodeURIComponent(userName)}&category=${category}`,
        formData,
        {
          ...authConfig(),
          headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" }
        }
      );

      // Award gamification points
      try {
        await axios.post(`${API}/events/${eventId}/gamification/award-points`, {
          user_name: userName,
          action_type: "selfie",
          points: 15
        }, authConfig());
      } catch (error) {
        console.error("Failed to award points:", error);
      }

      toast.success("Selfie hochgeladen! +15 Punkte 📸", { id: "selfie-upload" });
      localStorage.setItem("selfie_user_name", userName);
      fetchSelfies();
      fetchWinners();
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Fehler beim Hochladen", { id: "selfie-upload" });
    } finally {
      setUploading(false);
    }
  };

  const handleLike = async (selfieId) => {
    if (!userName.trim()) {
      toast.error("Bitte gib deinen Namen ein!");
      return;
    }

    try {
      await axios.post(`${API}/events/${eventId}/selfies/${selfieId}/like`, {
        voter_name: userName
      });

      // Award gamification points
      try {
        await axios.post(`${API}/events/${eventId}/gamification/award-points`, {
          user_name: userName,
          action_type: "like",
          points: 2
        });
      } catch (error) {
        console.error("Failed to award points:", error);
      }

      fetchSelfies();
      fetchWinners();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Du hast schon geliked!");
      } else {
        toast.error("Fehler beim Liken");
      }
    }
  };

  const handleUnlike = async (selfieId) => {
    if (!userName.trim()) return;

    try {
      await axios.delete(`${API}/events/${eventId}/selfies/${selfieId}/like?voter_name=${userName}`);
      fetchSelfies();
      fetchWinners();
    } catch (error) {
      console.error("Unlike failed:", error);
    }
  };

  const handleComment = async (selfieId) => {
    const comment = commentData[selfieId];
    if (!comment || !comment.trim()) return;
    if (!userName.trim()) {
      toast.error("Bitte gib deinen Namen ein!");
      return;
    }

    try {
      await axios.post(`${API}/events/${eventId}/selfies/${selfieId}/comment`, {
        author_name: userName,
        comment_text: comment
      });

      // Award gamification points
      try {
        await axios.post(`${API}/events/${eventId}/gamification/award-points`, {
          user_name: userName,
          action_type: "comment",
          points: 2
        });
      } catch (error) {
        console.error("Failed to award points:", error);
      }

      setCommentData({ ...commentData, [selfieId]: "" });
      toast.success("Kommentar hinzugefügt! +2 Punkte");
      fetchSelfies();
    } catch (error) {
      toast.error("Fehler beim Kommentieren");
    }
  };

  const handleDelete = async (selfieId) => {
    if (!confirm("Selfie wirklich löschen?")) return;

    try {
      await axios.delete(`${API}/events/${eventId}/selfies/${selfieId}`, authConfig());
      toast.success("Selfie gelöscht");
      fetchSelfies();
      fetchWinners();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const hasUserLiked = (selfie) => {
    return selfie.liked_by?.includes(userName);
  };

  const filteredSelfies = selectedCategory === "all"
    ? selfies
    : selfies.filter(s => s.category === selectedCategory);

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-2xl shadow-sm">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Activity Hub */}
      <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-pink-500 via-purple-600 to-indigo-700 p-6 md:p-8 text-white shadow-2xl transition-all duration-700">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-pink-400/20 rounded-full blur-[60px] translate-y-1/2 -translate-x-1/4" />

        <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 bg-white/15 backdrop-blur-xl rounded-[1.5rem] flex items-center justify-center border border-white/25 shadow-xl group-hover:rotate-6 transition-transform">
                <Camera className="w-8 h-8 text-white drop-shadow-md" />
              </div>
              <div>
                <h3 className="text-3xl font-black tracking-tighter leading-none mb-1">Selfie-Spiel 📸</h3>
                <p className="text-white/60 text-xs font-bold uppercase tracking-widest">Die ultimative Challenge</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl px-5 py-3 border border-white/10 flex flex-col items-center min-w-[80px]">
                <span className="text-[9px] font-black uppercase tracking-widest opacity-50 mb-1">Selfies</span>
                <span className="text-2xl font-black">{selfies.length}</span>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl px-5 py-3 border border-white/10 flex flex-col items-center min-w-[80px]">
                <span className="text-[9px] font-black uppercase tracking-widest opacity-50 mb-1">Gewinner</span>
                <span className="text-2xl font-black">{Object.keys(winners).length}</span>
              </div>
            </div>
          </div>

          <div className="mt-8">
            {!userName ? (
              <div className="bg-white/10 backdrop-blur-2xl p-6 rounded-[2rem] border border-white/20 shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-500">
                <p className="text-[10px] font-black uppercase tracking-[0.3em] mb-3 opacity-70">Bevor es losgeht: Wer bist du?</p>
                <div className="relative flex flex-col gap-3">
                  <div className="relative">
                    <Input
                      value={tempName}
                      onChange={(e) => setTempName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && tempName.trim()) {
                          setUserName(tempName);
                          localStorage.setItem("selfie_user_name", tempName);
                        }
                      }}
                      placeholder="Dein Name zum Mitmachen..."
                      className="bg-white/20 border-white/20 text-white placeholder:text-white/40 h-14 rounded-2xl focus:bg-white/30 transition-all font-bold pr-12 focus:ring-0 text-lg"
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30">
                      <User className="w-6 h-6" />
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (tempName.trim()) {
                        setUserName(tempName);
                        localStorage.setItem("selfie_user_name", tempName);
                      }
                    }}
                    disabled={!tempName.trim()}
                    className="w-full py-4 bg-white text-indigo-600 rounded-[1.5rem] font-black uppercase tracking-widest text-xs hover:bg-gray-100 transition-all shadow-xl disabled:opacity-50"
                  >
                    Jetzt Starten
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-white/10 backdrop-blur-lg px-6 py-4 rounded-2xl border border-white/15 hover:bg-white/20 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-black text-sm uppercase">
                    {userName?.charAt(0) || '?'}
                  </div>
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-widest opacity-50 block">Angemeldet als</span>
                    <span className="text-lg font-black">{userName}</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setUserName("");
                    localStorage.removeItem("selfie_user_name");
                  }}
                  className="p-3 bg-white/10 rounded-xl opacity-40 hover:opacity-100 hover:bg-red-500/50 hover:text-white transition-all"
                  title="Name ändern"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="upload" className="space-y-8 mt-4">
        <div className="bg-gray-100/50 backdrop-blur-md p-1.5 rounded-[2.5rem] border border-gray-100 shadow-inner">
          <TabsList className="grid w-full grid-cols-3 bg-transparent p-0 h-14 md:h-16">
            <TabsTrigger value="upload" className="rounded-[2rem] text-[10px] md:text-sm font-black uppercase tracking-widest transition-all duration-300 data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-lg border-2 border-transparent hover:text-indigo-500">
              <Upload className="w-4 h-4 md:w-5 md:h-5 md:mr-3 mr-2" />
              Upload
            </TabsTrigger>
            <TabsTrigger value="gallery" className="rounded-[2rem] text-[10px] md:text-sm font-black uppercase tracking-widest transition-all duration-300 data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-lg border-2 border-transparent hover:text-indigo-500">
              <Camera className="w-4 h-4 md:w-5 md:h-5 md:mr-3 mr-2" />
              Galerie
            </TabsTrigger>
            <TabsTrigger value="winners" className="rounded-[2rem] text-[10px] md:text-sm font-black uppercase tracking-widest transition-all duration-300 data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-lg border-2 border-transparent hover:text-indigo-500">
              <Trophy className="w-4 h-4 md:w-5 md:h-5 md:mr-3 mr-2" />
              Gewinner
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Upload Tab - Now First */}
        <TabsContent value="upload" className="space-y-6 pt-2 animate-in fade-in slide-in-from-top-2 duration-400">
          <div className="grid grid-cols-2 gap-4">
            {categories.slice(1).map(cat => (
              <div key={cat.value} className="group relative bg-white p-5 md:p-6 rounded-[2.5rem] shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:-translate-y-1 overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gray-50/50 rounded-full translate-x-1/2 -translate-y-1/2 group-hover:bg-indigo-50/50 transition-colors" />

                <div className="relative text-center mb-5">
                  <div className="text-4xl md:text-5xl mb-3 transform group-hover:scale-110 transition-transform duration-500">{cat.emoji}</div>
                  <h4 className="font-black text-gray-900 uppercase tracking-tight text-sm md:text-base">{cat.label}</h4>
                  <p className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1 opacity-70">
                    {cat.value === "funny" && "Lustigste Grimasse"}
                    {cat.value === "group" && "Mit Team/Freunden"}
                    {cat.value === "creative" && "Verrückte Ideen"}
                    {cat.value === "general" && "Einfach drauf los"}
                  </p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  capture="user"
                  className="hidden"
                  id={`upload-${cat.value}`}
                  onChange={(e) => handleUpload(e, cat.value)}
                  disabled={uploading || !userName.trim()}
                />
                <label
                  htmlFor={`upload-${cat.value}`}
                  className={`relative block w-full py-4 rounded-[1.5rem] text-center text-xs font-black uppercase tracking-widest cursor-pointer transition-all ${!userName.trim()
                    ? 'bg-gray-50 text-gray-300 cursor-not-allowed border border-gray-100'
                    : 'bg-[#121212] text-white hover:bg-indigo-600 hover:shadow-lg shadow-indigo-100 active:scale-95'
                    }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Camera className="w-4 h-4" />
                    Snap It
                  </div>
                </label>
              </div>
            ))}
          </div>

          {!userName.trim() && (
            <p className="text-center text-sm text-amber-600 bg-amber-50 p-3 rounded-xl">
              ⚠️ Bitte gib oben deinen Namen ein
            </p>
          )}
        </TabsContent>

        {/* Gallery Tab */}
        <TabsContent value="gallery" className="space-y-8 pt-2 animate-in fade-in slide-in-from-top-2 duration-400">
          <div className="flex gap-2 flex-wrap pb-2 border-b border-gray-100 mb-6 overflow-x-auto no-scrollbar">
            {categories.map(cat => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-6 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${selectedCategory === cat.value
                  ? 'bg-[#121212] text-white shadow-xl'
                  : 'bg-white border border-gray-100 text-gray-500 hover:bg-gray-50'
                  }`}
              >
                {cat.emoji} {cat.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredSelfies.length === 0 ? (
              <div className="col-span-full text-center py-24 bg-white rounded-[3rem] border-2 border-dashed border-gray-100">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-100">
                  <Camera className="w-8 h-8 text-gray-200" />
                </div>
                <p className="text-gray-900 font-black text-lg">Noch keine Selfies hier</p>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">Sei der Erste und teile einen Moment! 🎉</p>
              </div>
            ) : (
              filteredSelfies.map((selfie) => (
                <div key={selfie.id} className="bg-white rounded-[2.5rem] border border-gray-50 shadow-sm overflow-hidden group hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
                  {/* Image */}
                  <div className="relative aspect-square md:aspect-[4/5] overflow-hidden bg-gray-100">
                    <img
                      src={`${BACKEND_URL}${selfie.image_url}`}
                      alt={`Selfie von ${selfie.author_name}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    {isOwner && (
                      <Button
                        size="icon"
                        variant="destructive"
                        className="absolute top-4 right-4 h-10 w-10 rounded-2xl shadow-xl backdrop-blur-md bg-red-500/80 hover:bg-red-600 transition-all opacity-0 group-hover:opacity-100"
                        onClick={() => handleDelete(selfie.id)}
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    )}
                    <div className="absolute bottom-4 left-4">
                      <div className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full border border-white/30 text-[9px] font-black text-white uppercase tracking-widest">
                        {categories.find(c => c.value === selfie.category)?.emoji}{" "}
                        {categories.find(c => c.value === selfie.category)?.label}
                      </div>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center font-black text-xs text-gray-400">
                          {selfie.author_name?.charAt(0) || '?'}
                        </div>
                        <p className="font-black text-gray-900">{selfie.author_name}</p>
                      </div>
                      <button
                        onClick={() => hasUserLiked(selfie) ? handleUnlike(selfie.id) : handleLike(selfie.id)}
                        disabled={!userName.trim()}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-all ${hasUserLiked(selfie)
                          ? 'bg-red-500 text-white shadow-lg shadow-red-100'
                          : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                          }`}
                      >
                        <Heart className={`w-4 h-4 ${hasUserLiked(selfie) ? 'fill-current' : ''}`} />
                        {selfie.likes || 0}
                      </button>
                    </div>

                    {/* Comments */}
                    {selfie.comments && selfie.comments.length > 0 && (
                      <div className="mb-6 space-y-3 bg-gray-50/50 p-4 rounded-3xl border border-gray-50">
                        {selfie.comments.map((comment, idx) => (
                          <div key={idx} className="flex flex-col gap-1">
                            <span className="font-black text-[9px] text-indigo-600 uppercase tracking-widest">{comment.author_name}</span>
                            <p className="text-sm text-gray-700 leading-relaxed">{comment.comment_text}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Comment */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Schreib was Nettes..."
                        value={commentData[selfie.id] || ""}
                        onChange={(e) => setCommentData({ ...commentData, [selfie.id]: e.target.value })}
                        className="flex-1 h-12 bg-gray-50 border-transparent rounded-2xl px-4 focus:bg-white focus:border-gray-100 transition-all font-medium text-sm"
                        disabled={!userName.trim()}
                      />
                      <Button
                        size="icon"
                        className="h-12 w-12 rounded-2xl bg-[#121212] hover:bg-indigo-600 shadow-lg transition-all active:scale-90"
                        onClick={() => handleComment(selfie.id)}
                        disabled={!commentData[selfie.id] || !userName.trim()}
                      >
                        <MessageCircle className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Winners Tab */}
        <TabsContent value="winners" className="space-y-6 animate-in fade-in slide-in-from-top-2 duration-500">
          <div className="text-center py-4">
            <div className="w-20 h-20 bg-yellow-50 rounded-[2rem] flex items-center justify-center mx-auto mb-4 border border-yellow-100 shadow-inner">
              <Trophy className="w-10 h-10 text-yellow-500 animate-bounce" />
            </div>
            <h4 className="text-2xl font-black text-gray-900 tracking-tight">Hall of Fame 🏆</h4>
            <p className="text-sm text-gray-400 font-bold uppercase tracking-widest mt-1">Die beliebtesten Momente</p>
          </div>

          <div className="space-y-4">
            {winners?.overall && (
              <div className="group relative p-6 bg-gradient-to-br from-yellow-500 via-yellow-400 to-orange-500 rounded-[3rem] border-4 border-yellow-200 shadow-2xl overflow-hidden transition-all hover:scale-[1.02]">
                <div className="absolute -top-12 -right-12 w-32 h-32 bg-white/20 rounded-full blur-2xl" />

                <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
                  <div className="relative">
                    <div className="absolute -inset-1 bg-white/50 rounded-[1.5rem] blur opacity-40" />
                    <img
                      src={`${BACKEND_URL}${winners.overall.image_url}`}
                      alt="Winner"
                      className="relative w-32 h-32 object-cover rounded-[1.8rem] border-2 border-white/50 shadow-xl"
                    />
                    <div className="absolute -bottom-3 -right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-yellow-500">
                      <span className="text-xl">🏆</span>
                    </div>
                  </div>

                  <div className="text-center md:text-left text-white">
                    <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                      <span className="px-3 py-1 bg-white/20 backdrop-blur rounded-full text-[10px] font-black uppercase tracking-widest border border-white/30">Gesamtsieger</span>
                    </div>
                    <h5 className="text-2xl font-black mb-1">{winners.overall.author_name}</h5>
                    <div className="flex items-center justify-center md:justify-start gap-2">
                      <div className="px-3 py-1 bg-white/10 rounded-full flex items-center gap-1.5 border border-white/10">
                        <Heart className="w-3.5 h-3.5 fill-white text-white" />
                        <span className="text-sm font-black">{winners.overall.likes} Likes</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {categories.slice(1).map(cat => {
                const winner = winners?.[cat.value];
                if (!winner) return null;

                return (
                  <div key={cat.value} className="bg-white p-5 rounded-[2.5rem] border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-gray-50 rounded-2xl flex items-center justify-center text-xl shadow-inner group-hover:bg-brand-background transition-colors">
                        {cat.emoji}
                      </div>
                      <div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 block leading-none mb-1">Gewinner</span>
                        <h5 className="font-black text-gray-900 leading-none">{cat.label}</h5>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <img
                        src={`${BACKEND_URL}${winner.image_url}`}
                        alt={`${cat.label} Winner`}
                        className="w-20 h-20 object-cover rounded-2xl shadow-md border border-gray-50 group-hover:scale-105 transition-transform"
                      />
                      <div>
                        <p className="font-black text-lg text-gray-900 leading-tight mb-1">{winner.author_name}</p>
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-50 text-red-600 rounded-full border border-red-100">
                          <Heart className="w-3 h-3 fill-current" />
                          <span className="text-[10px] font-black">{winner.likes} Likes</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {(!winners || Object.keys(winners).length === 0) && (
              <div className="text-center py-12 text-muted-foreground">
                <Trophy className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Noch keine Gewinner</p>
                <p className="text-xs mt-2">Ladet Selfies hoch und votet!</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
